import CompoundTranslation from '@components/compound-translation';
import FormI9AddressFieldItems from '@containers/form-i9/address-field-items';
import {
    BorderedGrid,
    BorderedGridItem,
    FormSectionDivider,
    MaskedLabel,
    ReadonlyFieldLabel,
    ReadonlyFieldValue,
    SectionBox,
    SectionHeading,
    SectionSubtitle
} from '@containers/form-i9/styles';
import { ISection1CandidateDetail } from '@models/onboarding-review/section1-candidate-detail';
import TextMasks from '@models/text-masks';
import CitizenshipStatus from '@screens/review-section3/review/citizenship-status';
import { Title } from '@screens/review-section3/review/styles';
import { createTranslationService } from '@services/factories';
import { VerticalLayout } from '@styles/spacing-styles';
import { cacheTranslations } from '@utils/cache-translations';
import {
    DateFormatTemplate,
    formatAsUTC
} from '@utils/date-format';
import { defaultIfEmpty } from '@utils/string-utils';
import { observer } from 'mobx-react';
import * as React from 'react';

const formatUSSocialSecurityNumber = (usSocialSecurityNumber: string): React.ReactNode => {
    return (
        <MaskedLabel mask={TextMasks.usSocialSecurityNumberMask}
                     value={usSocialSecurityNumber}/>
    );
};

const formatPhoneNumber = (phoneNumber: string): React.ReactNode => {
    return (
        <MaskedLabel mask={TextMasks.phoneNumberMask}
                     value={phoneNumber}/>
    );
};

enum CandidateDetailsTranslation {
    startHereInstruction = 'startHereInstruction',
    antiDiscriminationInstructionTitle = 'antiDiscriminationInstructionTitle',
    antiDiscriminationInstructionCannot = 'antiDiscriminationInstructionCannot',
    antiDiscriminationInstructionTemplate = 'antiDiscriminationInstructionTemplate',
    i9Section1Heading = 'i9Section1Heading',
    i9Section1SubtitleTemplate = 'i9Section1SubtitleTemplate',
    i9Section1SubtitleFirstDayOfEmployment = 'i9Section1SubtitleFirstDayOfEmployment',
    lastNameLabel = 'lastNameLabel',
    firstNameLabel = 'firstNameLabel',
    middleInitialLabel = 'middleInitialLabel',
    otherLastNamesUsedLabel = 'otherLastNamesUsedLabel',
    dateOfBirthLabel = 'dateOfBirthLabel',
    usSocialSecurityNumberLabel = 'usSocialSecurityNumberLabel',
    emailAddressLabel = 'emailAddressLabel',
    phoneNumberLabel = 'phoneNumberLabel',
    citizenshipImmigrationStatusLabel = 'citizenshipImmigrationStatusLabel',
    expirationDateLabel = 'expirationDateLabel',
    foreignPassportLabel = 'foreignPassportLabel',
    countryOfIssuanceLabel = 'countryOfIssuanceLabel',
    qrCodeSection1Line1 = 'qrCodeSection1Line1',
    qrCodeSection1Line2 = 'qrCodeSection1Line2',
    employeeSignatureLabel = 'employeeSignatureLabel',
    todaysDateLabel = 'todaysDateLabel',
    identityStatementAttestation = 'identityStatementAttestation',
    notAvailable = 'notAvailable',
    alienRegistrationUSCISNumberLabel = 'alienRegistrationUSCISNumberLabel',
    formI94AdmissionNumberLabel = 'formI94AdmissionNumberLabel',
    unknownAlienAuthorizationLabel = 'unknownAlienAuthorizationLabel',
    unknownAlienAuthorization = 'unknownAlienAuthorization'
}

const CitizenshipDetails: React.FunctionComponent<{
    translations: Record<CandidateDetailsTranslation, string>,
    fields: ISection1CandidateDetail
}> = observer(props => {
    /*
     1. US Citizen => 1 box
     2. Non citizen national => 1 box
     3. Lawful permanent resident => 2 boxes
     4. Authorized Alien => 3-4 boxes
     */
    const hasFieldValue = (value: string): boolean => value?.length > 0;
    const renderAdditionalItemsByCitizenshipStatus = {
        '3': () => {
            const alienRegistrationUSCISNumber = [
                {
                    condition: hasFieldValue(props.fields.permanentResidentNumber),
                    value: props.fields.permanentResidentNumber
                }
            ].find(({ condition }) => condition === true)?.value ?? props.translations.notAvailable;

            return (
                <React.Fragment>
                    {/* Alien Registration Number / USCIS Number */}
                    <BorderedGridItem xs={12} sm={true}>
                        <ReadonlyFieldLabel>{props.translations.alienRegistrationUSCISNumberLabel}</ReadonlyFieldLabel>
                        <ReadonlyFieldValue>{alienRegistrationUSCISNumber}</ReadonlyFieldValue>
                    </BorderedGridItem>
                </React.Fragment>
            );
        },
        '4': () => {
            const requireCountryOfIssuance = (
                hasFieldValue(props.fields.foreignPassportNumber) &&
                !hasFieldValue(props.fields.formI94AdmissionNumber) &&
                !hasFieldValue(props.fields.permanentResidentNumber)
            );
            const {
                label,
                value
            } = [
                {
                    condition: hasFieldValue(props.fields.permanentResidentNumber),
                    label: props.translations.alienRegistrationUSCISNumberLabel,
                    value: props.fields.permanentResidentNumber
                },
                {
                    condition: hasFieldValue(props.fields.formI94AdmissionNumber),
                    label: props.translations.formI94AdmissionNumberLabel,
                    value: props.fields.formI94AdmissionNumber
                },
                {
                    condition: hasFieldValue(props.fields.foreignPassportNumber),
                    label: props.translations.foreignPassportLabel,
                    value: props.fields.foreignPassportNumber
                }
            ].find(({ condition }) => condition === true) ?? {
                label: props.translations.unknownAlienAuthorizationLabel,
                value: props.translations.unknownAlienAuthorization
            };

            return (
                <React.Fragment>
                    {/* Expiration Date */}
                    <BorderedGridItem xs={12} sm={true}>
                        <ReadonlyFieldLabel>{props.translations.expirationDateLabel}</ReadonlyFieldLabel>
                        <ReadonlyFieldValue>{props.fields.expirationDate}</ReadonlyFieldValue>
                    </BorderedGridItem>
                    {/* Alien Registration Number / USCIS Number / Form I-94 Admission Number / Foreign Passport Number */}
                    <BorderedGridItem xs={12} sm={true}>
                        <ReadonlyFieldLabel>{label}</ReadonlyFieldLabel>
                        <ReadonlyFieldValue>{value}</ReadonlyFieldValue>
                    </BorderedGridItem>
                    {/* Country of Issuance */}
                    {
                        requireCountryOfIssuance && (
                            <BorderedGridItem xs={12} sm={true}>
                                <ReadonlyFieldLabel>{props.translations.countryOfIssuanceLabel}</ReadonlyFieldLabel>
                                <ReadonlyFieldValue>{props.fields.countryOfIssuance || props.translations.notAvailable}</ReadonlyFieldValue>
                            </BorderedGridItem>
                        )
                    }
                </React.Fragment>
            );
        }
    };

    return (
        <React.Fragment>
            <BorderedGridItem xs={12} sm={true}>
                <ReadonlyFieldLabel>{props.translations.citizenshipImmigrationStatusLabel}</ReadonlyFieldLabel>
                <ReadonlyFieldValue>
                    <CitizenshipStatus status={props.fields.citizenshipStatus}
                                       template={'{0}. {1}'}
                                       fallbackLabel={props.translations.notAvailable}/>
                </ReadonlyFieldValue>
            </BorderedGridItem>
            {
                renderAdditionalItemsByCitizenshipStatus[props.fields.citizenshipStatus]?.() ?? null
            }
        </React.Fragment>
    );
});

const FormI9CandidateDetails: React.FunctionComponent<{
    fields: ISection1CandidateDetail;
    className?: string;
    style?: React.CSSProperties;
}> = observer(props => {
    const {
        fields,
        className,
        style
    } = props;

    const translations = cacheTranslations(
        createTranslationService(),
        CandidateDetailsTranslation,
        'candidateDetails'
    );

    return (
        <div className={className}
             style={style}>
            <FormSectionDivider/>
            <Title>{translations.startHereInstruction}</Title>
            <VerticalLayout bottomSpacing={'large'}>
                <CompoundTranslation template={translations.antiDiscriminationInstructionTemplate}
                                     data={[
                                         <b key={0}>{translations.antiDiscriminationInstructionTitle}</b>,
                                         <b key={1}>{translations.antiDiscriminationInstructionCannot}</b>
                                     ]}/>
            </VerticalLayout>
            <VerticalLayout innerSpacing={'small'}>
                <SectionBox>
                    <SectionHeading>
                        {translations.i9Section1Heading}
                    </SectionHeading>
                    <br/>
                    <SectionSubtitle>
                        <CompoundTranslation template={translations.i9Section1SubtitleTemplate}
                                             data={[
                                                 <SectionSubtitle key={0}
                                                                  emphasise={true}>
                                                     {translations.i9Section1SubtitleFirstDayOfEmployment}
                                                 </SectionSubtitle>
                                             ]}/>
                    </SectionSubtitle>
                </SectionBox>
                <div>
                    <BorderedGrid container={true}
                                  wrap='wrap'>
                        {/* Row 1 */}
                        <BorderedGridItem xs={12} sm={4}>
                            <ReadonlyFieldLabel>{translations.lastNameLabel}</ReadonlyFieldLabel>
                            <ReadonlyFieldValue>{defaultIfEmpty(fields.lastName, translations.notAvailable)}</ReadonlyFieldValue>
                        </BorderedGridItem>
                        <BorderedGridItem xs={12} sm={3}>
                            <ReadonlyFieldLabel>{translations.firstNameLabel}</ReadonlyFieldLabel>
                            <ReadonlyFieldValue>{defaultIfEmpty(fields.firstName, translations.notAvailable)}</ReadonlyFieldValue>
                        </BorderedGridItem>
                        <BorderedGridItem xs={2} sm={2}>
                            <ReadonlyFieldLabel>{translations.middleInitialLabel}</ReadonlyFieldLabel>
                            <ReadonlyFieldValue>{defaultIfEmpty(fields.middleInitial, translations.notAvailable)}</ReadonlyFieldValue>
                        </BorderedGridItem>
                        <BorderedGridItem xs={10} sm={3}>
                            <ReadonlyFieldLabel>{translations.otherLastNamesUsedLabel}</ReadonlyFieldLabel>
                            <ReadonlyFieldValue>{defaultIfEmpty(fields.otherLastNames, translations.notAvailable)}</ReadonlyFieldValue>
                        </BorderedGridItem>
                        {/* Row 2 */}
                        <FormI9AddressFieldItems fields={fields}/>
                        {/* Row 3 */}
                        <BorderedGridItem xs={6} sm={2}>
                            <ReadonlyFieldLabel>{translations.dateOfBirthLabel}</ReadonlyFieldLabel>
                            <ReadonlyFieldValue>
                                {fields.dateOfBirth && formatAsUTC(fields.dateOfBirth, DateFormatTemplate.MonthFirstCalendarDate)}
                            </ReadonlyFieldValue>
                        </BorderedGridItem>
                        <BorderedGridItem xs={6} sm={4}>
                            <ReadonlyFieldLabel>{translations.usSocialSecurityNumberLabel}</ReadonlyFieldLabel>
                            <ReadonlyFieldValue>{fields.socialSecurityNumber && formatUSSocialSecurityNumber(fields.socialSecurityNumber)}</ReadonlyFieldValue>
                        </BorderedGridItem>
                        <BorderedGridItem xs={12} sm={3}>
                            <ReadonlyFieldLabel>{translations.emailAddressLabel}</ReadonlyFieldLabel>
                            <ReadonlyFieldValue>{fields.emailAddress}</ReadonlyFieldValue>
                        </BorderedGridItem>
                        <BorderedGridItem xs={12} sm={3}>
                            <ReadonlyFieldLabel>{translations.phoneNumberLabel}</ReadonlyFieldLabel>
                            <ReadonlyFieldValue>{fields.phoneNumber && formatPhoneNumber(fields.phoneNumber)}</ReadonlyFieldValue>
                        </BorderedGridItem>
                    </BorderedGrid>
                    <Title>
                        {translations.identityStatementAttestation}
                    </Title>
                    <BorderedGrid container={true}
                                  wrap='wrap'>
                        {/* Row 1 */}
                        <CitizenshipDetails translations={translations}
                                            fields={fields}/>
                        {/* Row 2 */}
                        <BorderedGridItem xs={12} sm={12}>
                            <ReadonlyFieldLabel>
                                {translations.qrCodeSection1Line1}
                                <br/>
                                {translations.qrCodeSection1Line2}
                            </ReadonlyFieldLabel>
                        </BorderedGridItem>
                    </BorderedGrid>
                </div>
                <BorderedGrid container={true}
                              wrap='wrap'>
                    <BorderedGridItem xs={12} sm={8}>
                        <ReadonlyFieldLabel>{translations.employeeSignatureLabel}</ReadonlyFieldLabel>
                        <ReadonlyFieldValue fontStyle='italic'>{fields.candidateSignature}</ReadonlyFieldValue>
                    </BorderedGridItem>
                    <BorderedGridItem xs={12} sm={4}>
                        <ReadonlyFieldLabel>{translations.todaysDateLabel}</ReadonlyFieldLabel>
                        <ReadonlyFieldValue>
                            {formatAsUTC(fields.section1SignedOn, DateFormatTemplate.MonthFirstCalendarDate)}
                        </ReadonlyFieldValue>
                    </BorderedGridItem>
                </BorderedGrid>
            </VerticalLayout>
        </div>
    );
});

export default FormI9CandidateDetails;



// WEBPACK FOOTER //
// ./src/containers/form-i9/candidate-details.tsx